import {
  Routes,
  Route,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import type { FallbackProps } from 'react-error-boundary';

import HomePage from './components/HomePage';
import RentalDetail from './components/RentalDetail';
import AboutPage from './components/AboutPage';
import EvergreenLandingPage from './components/EvergreenLandingPage';
import { ErrorFallback } from './components/ErrorFallback';
import { AlertConfirmPage, AlertManagePage, AlertUnsubscribePage } from './components/AlertPages';

import './index.css'

function ModalErrorFallback({ resetErrorBoundary }: FallbackProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { backgroundLocation?: Location } | null;
  const dismiss = () => {
    resetErrorBoundary();
    if (state?.backgroundLocation) navigate(-1);
    else navigate('/', { replace: true });
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-center items-start overflow-y-auto">
      <div className="bg-white max-w-lg w-full p-8 mt-16 rounded-2xl shadow-xl relative">
        <button onClick={dismiss} className="absolute top-3 right-4 text-gray-500 hover:text-gray-700 text-2xl">×</button>
        <p className="text-red-500 mt-4 mb-4">Something went wrong loading this rental.</p>
        <button onClick={dismiss} className="text-sm text-blue-600 hover:text-blue-800 underline">← Back to listings</button>
      </div>
    </div>
  );
}

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as { backgroundLocation?: Location };
  const backgroundLocation = state?.backgroundLocation;
  const directRentalPath = !backgroundLocation && location.pathname.startsWith('/rental/');
  const routeBackgroundLocation = backgroundLocation || (directRentalPath
    ? { pathname: '/', search: '', hash: '', state: null, key: 'direct-rental-background' }
    : null);
  const dismissRental = () => {
    if (backgroundLocation) navigate(-1);
    else navigate('/', { replace: true });
  };

  return (
    <>
      <Routes location={routeBackgroundLocation || location}>
        <Route path="/" element={<HomePage suspendUrlSync={Boolean(routeBackgroundLocation)} />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/rentals/:slug" element={<EvergreenLandingPage />} />
        <Route path="/alerts/confirm" element={<AlertConfirmPage />} />
        <Route path="/alerts/manage" element={<AlertManagePage />} />
        <Route path="/alerts/unsubscribe" element={<AlertUnsubscribePage />} />
        <Route path="/rental/:id" element={
          <ErrorBoundary key={location.pathname} FallbackComponent={ErrorFallback}>
            <RentalDetail />
          </ErrorBoundary>
        } />
      </Routes>

      {routeBackgroundLocation && (
        <ErrorBoundary key={location.pathname} FallbackComponent={ModalErrorFallback}>
          <Routes>
            <Route path="/rental/:id" element={<RentalDetail isModal onDismiss={dismissRental} />} />
          </Routes>
        </ErrorBoundary>
      )}
    </>
  );
}

export default App;
